import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { User, Mail, Lock, Eye, EyeOff, CheckCircle, AlertCircle, Gift } from 'lucide-react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageWrapper from '../components/PageWrapper';
import SEOMeta from '../components/SEOMeta';
import { validateGamePassword, PASSWORD_MAX_LEN, PASSWORD_MIN_LEN, PASSWORD_HINT } from '../lib/passwordRules';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? `${window.location.protocol}//${window.location.hostname}:8001`;

function formatError(detail) {
  if (!detail) return 'Algo salió mal. Inténtalo de nuevo.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(' ');
  return String(detail);
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ username: '', email: '', password: '', confirm: '' });
  const [refCode, setRefCode] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) setRefCode(ref.toUpperCase());
  }, [searchParams]);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.username.length < 3) {
      setError('El nombre de usuario debe tener al menos 3 caracteres.');
      return;
    }
    if (form.username.length > 16) {
      setError('El nombre de usuario no puede superar los 16 caracteres.');
      return;
    }
    if (form.password !== form.confirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    const passwordError = validateGamePassword(form.password);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    if (!agreed) {
      setError('Debes aceptar los Términos de Servicio.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/register`,
        { username: form.username, email: form.email, password: form.password, ref_code: refCode || undefined },
        { withCredentials: true }
      );
      if (window.gtag) {
        window.gtag('event', 'sign_up', { method: 'web' });
      }
      setSuccess(true);
      setTimeout(() => navigate('/encuesta?welcome=1'), 2000);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrapper scrollable>
      <SEOMeta title="Crear cuenta" noIndex />
      <Navbar />

      <main className="register-main anim-fade-up" style={{ animationDelay: '0.2s' }}>
        {success ? (
          <div className="register-card" data-testid="register-success">
            <CheckCircle size={52} className="success-icon" />
            <h2 className="register-title">¡Bienvenido a la Plaga!</h2>
            <p className="register-success-text">
              Tu cuenta ha sido creada. Te llevamos a una breve encuesta (opcional, 4 preguntas)
              para ganar <strong style={{ color: '#22d3ee' }}>100 Cristales de Escarcha</strong>...
            </p>
          </div>
        ) : (
          <div className="register-card" data-testid="register-card">
            {/* Header */}
            <div className="register-header">
              <p className="register-eyebrow">SERVIDOR WRATH OF THE LICH KING</p>
              <h1 className="register-title" data-testid="register-heading">CREAR CUENTA</h1>
              <p className="register-subtitle">Tu destino en Azeroth te aguarda.</p>
            </div>

            {/* Error */}
            {error && (
              <div className="register-error" data-testid="register-error">
                <AlertCircle size={15} />
                <span>{error}</span>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="register-form" data-testid="register-form">
              {/* Username */}
              <div className="form-field">
                <label htmlFor="reg-username" className="form-label">NOMBRE DE USUARIO</label>
                <div className="input-wrap">
                  <User size={15} className="input-icon" />
                  <input
                    type="text"
                    id="reg-username"
                    name="username"
                    value={form.username}
                    onChange={handleChange}
                    placeholder="Tu héroe de Azeroth"
                    className="form-input"
                    data-testid="input-username"
                    required
                    maxLength={16}
                    autoComplete="username"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="form-field">
                <label htmlFor="reg-email" className="form-label">CORREO ELECTRÓNICO</label>
                <div className="input-wrap">
                  <Mail size={15} className="input-icon" />
                  <input
                    type="email"
                    id="reg-email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="tu@correo.com"
                    className="form-input"
                    data-testid="input-email"
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="form-field">
                <label htmlFor="reg-password" className="form-label">CONTRASEÑA</label>
                <div className="input-wrap">
                  <Lock size={15} className="input-icon" />
                  <input
                    type={showPass ? 'text' : 'password'}
                    id="reg-password"
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    placeholder={PASSWORD_HINT}
                    className="form-input"
                    data-testid="input-password"
                    required
                    minLength={PASSWORD_MIN_LEN}
                    maxLength={PASSWORD_MAX_LEN}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="pass-toggle"
                    onClick={() => setShowPass(!showPass)}
                    data-testid="toggle-password"
                    tabIndex={-1}
                  >
                    {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="form-field">
                <label htmlFor="reg-confirm" className="form-label">CONFIRMAR CONTRASEÑA</label>
                <div className="input-wrap">
                  <Lock size={15} className="input-icon" />
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    id="reg-confirm"
                    name="confirm"
                    value={form.confirm}
                    onChange={handleChange}
                    placeholder="Repite la contraseña"
                    className="form-input"
                    data-testid="input-confirm-password"
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="pass-toggle"
                    onClick={() => setShowConfirm(!showConfirm)}
                    data-testid="toggle-confirm-password"
                    tabIndex={-1}
                  >
                    {showConfirm ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>

              {/* Referral code */}
              <div className="form-field">
                <label htmlFor="reg-refcode" className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  CÓDIGO DE REFERIDO
                  <span style={{ color: 'rgba(224,247,255,0.35)', fontWeight: 400, fontSize: 10 }}>(OPCIONAL)</span>
                </label>
                <div className="input-wrap">
                  <Gift size={15} className="input-icon" />
                  <input
                    id="reg-refcode"
                    type="text"
                    value={refCode}
                    onChange={(e) => setRefCode(e.target.value.toUpperCase())}
                    placeholder="Código de un amigo"
                    className="form-input"
                    maxLength={8}
                    autoComplete="off"
                  />
                </div>
                {refCode.length === 8 && (
                  <p style={{ color: '#4ade80', fontSize: 11, marginTop: 4 }}>
                    ¡Recibirás 100 Cristales de Escarcha de bienvenida!
                  </p>
                )}
              </div>

              {/* Terms */}
              <label className="form-terms" data-testid="terms-checkbox-label">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="terms-checkbox"
                  data-testid="terms-checkbox"
                />
                <span>
                  Acepto los{' '}
                  <Link to="/terminos" className="terms-link" target="_blank" rel="noopener noreferrer">Términos de Servicio</Link>
                  {' '}y la{' '}
                  <Link to="/privacidad" className="terms-link" target="_blank" rel="noopener noreferrer">Política de Privacidad</Link>
                </span>
              </label>

              {/* Submit */}
              <button
                type="submit"
                className={`register-btn ${loading ? 'register-btn--loading' : ''}`}
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? 'CREANDO CUENTA...' : 'UNIRSE A LA PLAGA'}
              </button>
            </form>

            <p className="register-login-link">
              ¿Ya tienes cuenta?{' '}
              <Link to="/login" className="terms-link" data-testid="login-link">
                Iniciar Sesión
              </Link>
            </p>
          </div>
        )}
      </main>

      <Footer />
    </PageWrapper>
  );
}
